// ThemeContext.js
import React, { createContext, useState, useMemo, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { themeColors } from "../utilities/themeColors";
export const ThemeContext = createContext();

export const ThemeContextProvider = ({ children }) => {
    const [mode, setMode] = useState(localStorage.getItem("themeMode") || "light");
    const [accentColor, setAccentColor] = useState(localStorage.getItem("accentColor") || "blue");

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: themeColors[accentColor],
                    background: {
                        default: mode === "light" ? "#fafafa" : "#303030",
                        paper: mode === "light" ? "#ffffff" : "#424242",
                    },
                    text: {
                        primary: mode === "light" ? "#000000" : "#ffffff",
                    },
                },
                typography: {
                    fontFamily: "'Poppins', sans-serif",
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                fontFamily: "'Poppins', sans-serif",
                            },
                            "*": {
                                fontFamily: "'Poppins', sans-serif",
                            },
                        },
                    },
                },
            }),
        [mode, accentColor]
    );

    useEffect(() => {
        const root = document.documentElement;

        // Primary color variables
        root.style.setProperty("--primary-main", theme.palette.primary.main);
        root.style.setProperty("--primary-light", theme.palette.primary.light);
        root.style.setProperty("--primary-dark", theme.palette.primary.dark);
        root.style.setProperty("--primary-contrast-text", theme.palette.primary.contrastText);
        root.style.setProperty("--primary-hover", theme.palette.primary.light);
        root.style.setProperty("--primary-selected", theme.palette.primary.dark);
        root.style.setProperty("--primary-disabled", theme.palette.primary.light);

        // Font family variable
        root.style.setProperty("--font-family", theme.typography.fontFamily);

        // Font size variables
        root.style.setProperty("--font-size-base", `${theme.typography.fontSize}px`);
        root.style.setProperty("--font-size-h1", theme.typography.h1.fontSize);
        root.style.setProperty("--font-size-h2", theme.typography.h2.fontSize);
        root.style.setProperty("--font-size-h3", theme.typography.h3.fontSize);
        root.style.setProperty("--font-size-body1", theme.typography.body1.fontSize);
        root.style.setProperty("--font-size-body2", theme.typography.body2.fontSize);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("themeMode", mode);
        localStorage.setItem("accentColor", accentColor);
    }, [mode, accentColor]);

    const toggleMode = () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    };

    const changeAccentColor = (color) => {
        setAccentColor(color);
    };

    return (
        <ThemeContext.Provider value={{ toggleMode, changeAccentColor, mode, accentColor }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
